import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import ActionModalHeader from '@/components/ui/ActionModalHeader';
import {
  ACTION_MODAL_BODY_CLASS,
  ACTION_MODAL_CONTENT_CLASS,
} from '@/components/booking/paymentScheduleModalStyles';

/**
 * Full-screen action overlay shell (framer-motion). Matches Payment Schedule
 * action modals: #0b0b10 panel, glass header band, rounded-3xl, blur backdrop.
 *
 * Portals to document.body so overlays sit above the fixed app nav and are
 * never clipped by trip-detail scroll containers.
 */
export default function ActionModalShell({
  isOpen,
  onClose,
  icon,
  title,
  description,
  children,
  footer = null,
  maxWidthClass = 'sm:max-w-md',
  zIndexClass = 'z-[100]',
  bodyClassName = '',
  /** 'center' keeps the sheet in the viewport on mobile; 'bottom' is a slide-up sheet */
  mobilePlacement = 'center',
}) {
  if (typeof document === 'undefined') return null;

  const overlay = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cn(
            'fixed inset-0 flex justify-center overflow-y-auto overscroll-contain',
            mobilePlacement === 'bottom'
              ? 'items-end'
              : 'items-center',
            'px-3 pt-[max(4.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))]',
            'sm:items-center sm:p-4 sm:pt-4 sm:pb-4',
            zIndexClass,
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            type="button"
            aria-label="Close"
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="action-modal-title"
            className={cn(
              ACTION_MODAL_CONTENT_CLASS,
              maxWidthClass,
              'relative w-full my-auto',
            )}
            initial={{ y: 16, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3.5 right-3.5 z-10 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/[0.08] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <ActionModalHeader
              icon={icon}
              title={title}
              description={description}
              variant="plain"
            />
            <div className={cn(ACTION_MODAL_BODY_CLASS, bodyClassName)} id="action-modal-title">
              {children}
            </div>
            {footer && (
              <div className="flex-shrink-0 border-t border-white/[0.06] px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 sm:pb-4">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(overlay, document.body);
}
